import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useTable, usePagination, useFilters } from 'react-table';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import debounce from 'lodash/debounce';
import { classNames } from '@geerd-nx/utils';

export interface TableProps {
  paginated?: boolean;
  stateBasedPagination?: {
    pagination: { pageIndex: number; pageSize: number };
    setPagination: Dispatch<
      SetStateAction<{ pageIndex: number; pageSize: number }>
    >;
  };
  columns: Array<any>;
  data: Array<any>;
  InsertedRow?: React.ReactElement;
  update?: () => void;
  checked?: number | null | string;
  pageCount?: number;
  setters?: { [key: string]: (value: any) => void };
  hiddenColumns?: string[];
  canClickRow?: boolean;
  onRowClick?: (row?: any) => void;
  onSelectRow?: (row?: any) => void;
  onCheckedRow?: (row?: any) => void;
  totalItems?: number;
}

const Table: React.FC<TableProps> = ({
  onSelectRow,
  onCheckedRow,
  columns,
  checked,
  data,
  InsertedRow,
  update,
  pageCount: controlledPageCount,
  setters,
  hiddenColumns = [],
  canClickRow = true,
  onRowClick = () => false,
  paginated = true,
  stateBasedPagination,
  totalItems,
}) => {
  const [SearchParams, setSearchParams] = useSearchParams();
  const initialPageIndex = stateBasedPagination
    ? stateBasedPagination.pagination.pageIndex
    : parseInt(SearchParams.get('page') || '0'); // default to 0 if the query param is not a number  (or is missing) ;
  const initialPageSize = stateBasedPagination
    ? stateBasedPagination.pagination.pageSize
    : parseInt(SearchParams.get('pageSize') || '10'); // default to 0 if the query param is not a number  (or is missing) ;
  const node = useRef<HTMLDivElement>(null);
  function DefaultColumnFilter({ column }: any) {
    const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      setters && setters[column.getHeaderProps().key](`${e.target.value}`);
    };
    const debouncedChangeHandler = debounce(changeHandler, 300);
    // eslint-disable-next-line no-prototype-builtins
    if (setters?.hasOwnProperty(column.getHeaderProps().key))
      return (
        <input
          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
          type="text"
          onChange={debouncedChangeHandler}
          placeholder={`Search records...`}
        />
      );
    return null;
  }
  const defaultColumn = {
    Filter: DefaultColumnFilter,
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setPageSize,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      manualPagination: true, // Tell the usePagination
      initialState: {
        pageIndex: initialPageIndex,
        pageSize: initialPageSize,
        hiddenColumns,
      }, // Pass our hoisted table state
      pageCount: controlledPageCount ? Math.ceil(controlledPageCount) : 0,
    },
    useFilters,
    usePagination
  );
  useEffect(() => {
    if (!paginated) return;
    if (stateBasedPagination) {
      stateBasedPagination.setPagination({ pageIndex, pageSize });
    } else {
      const queries: any = {};
      for (const [entry, value] of Array.from(SearchParams.entries())) {
        queries[entry] = value;
      }
      setSearchParams(
        { ...queries, page: pageIndex, pageSize },
        { replace: true }
      );
    }
  }, [SearchParams, pageIndex, pageSize, paginated, setSearchParams]);

  return (
    <div ref={node}>
      <table className="min-w-full" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {onSelectRow && (
                <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"></th>
              )}
              {onCheckedRow && (
                <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"></th>
              )}
              {headerGroup.headers.map((column) => (
                <th
                  className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                  {...column.getHeaderProps()}
                >
                  {column.render('Header')}
                  <div className="mt-1">
                    {column.canFilter ? column.render('Filter') : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="bg-white" {...getTableBodyProps()}>
          <AnimatePresence>
            {page.map((row) => {
              prepareRow(row);
              return (
                <React.Fragment key={row.id}>
                  <motion.tr
                    transition={{ type: 'spring' }}
                    exit={{ opacity: 0, maxHeight: 0 }}
                    {...row.getRowProps()}
                    className={classNames(
                      canClickRow ? 'cursor-pointer hover:shadow-lg' : ''
                    )}
                    onClick={() => canClickRow && onRowClick(row.original)}
                  >
                    {onSelectRow && (
                      <motion.td className="relative w-12 sm:w-16 sm:px-8 px-6 py-4 whitespace-no-wrap text-sm border-b border-gray-200">
                        {checked === (row.original as any).id ? (
                          <input
                            id={row.id}
                            name="notification-method"
                            type="radio"
                            onChange={() => {
                              onSelectRow(row);
                            }}
                            checked
                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded-full border-gray-300 text-primary-600 focus:ring-primary-500 sm:left-6"
                          />
                        ) : (
                          <input
                            id={row.id}
                            name="notification-method"
                            type="radio"
                            onChange={() => {
                              onSelectRow(row);
                            }}
                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded-full border-gray-300 text-primary-600 focus:ring-primary-500 sm:left-6"
                          />
                        )}
                      </motion.td>
                    )}
                    {onCheckedRow && (
                      <motion.td className="relative w-12 sm:w-16 sm:px-8 px-6 py-4 whitespace-no-wrap text-sm border-b border-gray-200">
                        {
                          <input
                            id={row.id}
                            name="select-method"
                            type="checkbox"
                            onChange={() => {
                              onCheckedRow(row);
                            }}
                            checked={(row.original as any).enabled}
                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4  border-gray-300 text-primary-600 focus:ring-primary-500 sm:left-6"
                          />
                        }
                      </motion.td>
                    )}
                    {row.cells.map((cell) => {
                      return (
                        <motion.td
                          transition={{ type: 'spring' }}
                          className="px-6 py-4 whitespace-no-wrap text-sm border-b border-gray-200"
                          {...cell.getCellProps()}
                        >
                          {cell.render('Cell')}
                        </motion.td>
                      );
                    })}
                  </motion.tr>
                </React.Fragment>
              );
            })}
          </AnimatePresence>
        </tbody>
      </table>
      {InsertedRow}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="hidden sm:block">
          {paginated ? (
            <p className="text-sm leading-5 text-gray-700">
              Showing
              <span className="font-medium"> {pageIndex * pageSize + 1} </span>
              to
              <span className="font-medium">
                {' '}
                {pageSize * pageIndex + pageSize}{' '}
              </span>
              of
              <span className="font-medium">
                {' '}
                {totalItems
                  ? totalItems
                  : (controlledPageCount || 0) * pageSize}{' '}
              </span>
              results
            </p>
          ) : (
            <p className="text-sm leading-5 text-gray-700">
              <span className="font-medium">{data.length} </span>
              results
            </p>
          )}
        </div>
        {paginated && (
          <>
            <div>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value))}
                aria-label="Selected tab"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              >
                <option value={5}>5 rows</option>
                <option value={10}>10 rows</option>
                <option value={20}>20 rows</option>
                <option value={50}>50 rows</option>
              </select>
            </div>
            <div className="flex justify-between sm:justify-end">
              <button
                type="button"
                onClick={() => previousPage()}
                className={`relative inline-flex ${
                  !canPreviousPage && 'cursor-not-allowed'
                } items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white ${
                  canPreviousPage && 'hover:text-gray-500'
                } focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150`}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => nextPage()}
                className={`ml-3 relative inline-flex ${
                  !canNextPage && 'cursor-not-allowed'
                } disabled:cursor-not-allowed items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white ${
                  canNextPage && 'hover:text-gray-500'
                } focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150`}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export { Table };
